import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui';
import { toast } from '@/components/ui/Toast';
import { agentApi } from '@/shared/api/agentApi';

/**
 * עצירה/המשך של החייגן בקמפיין אחד, במסך העבודה.
 *
 * 🔑 עצירה **חייבת סיבה** (אפיון 5.3): השרת מחזיר 422 בלעדיה, ולכן
 * הכפתור פותח בורר סיבה במקום לשלוח ולקבל שגיאה. המשך הוא מיידי — אין
 * מה לתעד בחזרה לעבודה.
 *
 * המצב נקרא מ-`dialer/state` ולא מוחזק מקומית, כדי שעצירה ממסך אחר
 * (הסרגל העליון) תשתקף כאן מיד.
 */
export function CampaignDialControl({ campaignId, onChange }: {
  campaignId: number;
  /** מצב ההשהיה השתנה (מכאן או ממסך אחר) — מסך הסבבים עוצר את הלולאה. */
  onChange?: (paused: boolean) => void;
}) {
  const [paused, setPaused] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [reasons, setReasons] = useState<Array<{ code: string; label: string }>>([]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastRef = useRef<boolean | null>(null);

  const report = useCallback((next: boolean | null) => {
    if (next !== null && next !== lastRef.current) {
      lastRef.current = next;
      onChangeRef.current?.(next);
    }
  }, []);

  const load = useCallback(() => {
    agentApi.dialerState()
      .then((s) => {
        const c = (s?.campaigns ?? []).find((x) => x.id === campaignId);
        const next = c ? c.paused : null;
        setPaused(next);
        report(next);
      })
      .catch(() => {});
  }, [campaignId, report]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // הקמפיין אינו חייגן אוטומטי — אין מה לעצור.
  if (paused === null) return null;

  async function openPicker() {
    if (reasons.length === 0) {
      try {
        setReasons(await agentApi.dialerPauseReasons());
      } catch {
        // גם בלי הרשימה מהשרת חייבים לאפשר עצירה — נופלים לסיבות הבסיס.
        setReasons([
          { code: 'break', label: 'הפסקה' },
          { code: 'lunch', label: 'ארוחת צהריים' },
          { code: 'end_of_day', label: 'סוף יום' },
        ]);
      }
    }
    setPicking(true);
  }

  async function apply(next: boolean, reason?: string) {
    setBusy(true);
    try {
      await agentApi.dialerPause(campaignId, next, reason);
      setPaused(next);
      report(next);
      setPicking(false);
      toast(next ? 'החייגן נעצר' : 'החייגן ממשיך', 'ok');
    } catch {
      toast(next ? 'העצירה נכשלה' : 'ההמשך נכשל', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {paused ? (
        <button
          type="button"
          className="btn btn-sm primary"
          onClick={() => void apply(false)}
          disabled={busy}
          aria-label="המשך חיוג"
          title="המשך חיוג"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="play" size={14} /> המשך
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => void openPicker()}
          disabled={busy}
          aria-label="עצירת חיוג"
          title="עצירת חיוג — נדרשת סיבה"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="pause" size={14} /> עצור
        </button>
      )}

      {picking && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="סיבת עצירה"
          style={{
            position: 'fixed', inset: 0, zIndex: 2100,
            background: 'oklch(0% 0 0 / 0.45)', display: 'grid', placeItems: 'center',
            padding: 'var(--gap)',
          }}
          onClick={() => !busy && setPicking(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 'var(--r-4)',
              border: '1px solid var(--line)', padding: 'var(--pad-card)',
              width: '100%', maxWidth: 340,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>עצירת החייגן</div>
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 12 }}>
              יש לבחור סיבה — היא נרשמת ביומן.
            </div>

            <div className="stack" style={{ gap: 6 }}>
              {reasons.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => void apply(true, r.code)}
                  style={{ justifyContent: 'flex-start' }}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" className="btn btn-sm" onClick={() => setPicking(false)} disabled={busy}>
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
